/**
 * SOLID Review
 * S — Strong. One concern: the legal side's use cases — the documents, the re-acceptance gate, the GDPR acts, cookie consent —
 *     built once.
 * O — Good. A new legal act is one use case built here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the legal ops of `ClientUseCases`, and the consent platform's deliveries.
 * D — Good. Port interfaces, the account's sign-out and the shared steps, injected; what it builds is held by role (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  IPayloadLegalDocuments, IEntityLegalReview, ICommandLegalAcceptance, IPayloadDataExport, ICommandDeleteAccount,
} from '../../domain/ports/schemas/LegalMoves';
import type { IPayloadConsentState, ICommandCookiePreferences, IEntityPreferencesOpened } from '../../domain/ports/schemas/ConsentMoves';
import type { IPortClientLegalServer, IPortClientConsentPlatform } from '../ports/contracts';
import type { TPortOutcome, TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { PassThroughUseCase } from '../shared/PassThroughUseCase';
import type { IConfigDepLegal } from './IConfigDepLegal';
import { CheckLegalReviewUseCase } from './CheckLegalReviewUseCase';
import { AcceptLegalTermsUseCase } from './AcceptLegalTermsUseCase';
import { DeleteAccountUseCase } from './DeleteAccountUseCase';
import { ConsentChangedUseCase } from './ConsentChangedUseCase';

export interface IConfigDepLegalContext {
  readonly decisions: IPortClientDecisions;
  readonly legal: IPortClientLegalServer;
  /** The consent management platform (FE4b). */
  readonly consent: IPortClientConsentPlatform;
  /** The account's sign-out use case — a deleted account is a signed-out tab. */
  readonly account: IUseCase<ICommandNothing, TPortOutcome>;
  /** The one serial section, shared with the table and the session. */
  readonly steps: ClientSteps;
}

/**
 * THE LEGAL SIDE (frontend strict-ports plan, FE4a/FE4b, 2026-10-04; use cases since frontend-application-oo P4). The legal
 * modal's tabs, the GDPR export and deletion, the re-acceptance gate and the cookie consent called the legal REST adapter and
 * the CMP from React, and the gate decided inline. The calls are here; the rules are the Domain's (`LegalReview`, `CookieConsent`).
 */
export class LegalContext {
  /** The legal modal's tabs: every current document. */
  readonly loadLegalDocuments: IUseCase<ICommandNothing, TPortResult<IPayloadLegalDocuments>>;
  readonly checkLegalReview: IUseCase<ICommandNothing, TPortResult<IEntityLegalReview>>;
  readonly acceptLegalTerms: IUseCase<ICommandLegalAcceptance, TPortOutcome>;
  /** GDPR Art. 20: the player's data, for the screen to download. */
  readonly exportMyData: IUseCase<ICommandNothing, TPortResult<IPayloadDataExport>>;
  /** GDPR Art. 17, step one: a verification code by email. */
  readonly requestAccountDeletion: IUseCase<ICommandNothing, TPortOutcome>;
  readonly deleteAccount: IUseCase<ICommandDeleteAccount, TPortOutcome>;
  /** Boot the consent platform (idempotent at the platform: StrictMode mounts the provider twice). */
  readonly startConsent: IUseCase<ICommandNothing, TPortOutcome>;
  readonly openCookiePreferences: IUseCase<ICommandNothing, TPortResult<IEntityPreferencesOpened>>;
  readonly saveCookiePreferences: IUseCase<ICommandCookiePreferences, TPortOutcome>;
  /** The CMP's state, delivered — the handler `ServerEvents` hands a `consentChanged` to. */
  readonly consentChanged: IUseCase<IPayloadConsentState, TPortOutcome>;

  constructor(deps: IConfigDepLegalContext) {
    const { legal, consent } = deps;
    const side: IConfigDepLegal = { ...deps, step: deps.steps.onSession };
    this.loadLegalDocuments = new PassThroughUseCase<ICommandNothing, TPortResult<IPayloadLegalDocuments>>((ctx) => legal.documents(ctx));
    this.checkLegalReview = new CheckLegalReviewUseCase(side);
    this.acceptLegalTerms = new AcceptLegalTermsUseCase(side);
    this.exportMyData = new PassThroughUseCase<ICommandNothing, TPortResult<IPayloadDataExport>>((ctx) => legal.exportMyData(ctx));
    this.requestAccountDeletion = new PassThroughUseCase<ICommandNothing, TPortOutcome>((ctx) => legal.requestAccountDeletionCode(ctx));
    this.deleteAccount = new DeleteAccountUseCase(side);
    this.startConsent = new PassThroughUseCase<ICommandNothing, TPortOutcome>((ctx) => consent.start(ctx));
    this.openCookiePreferences = new PassThroughUseCase<ICommandNothing, TPortResult<IEntityPreferencesOpened>>((ctx) => consent.openPreferences(ctx));
    this.saveCookiePreferences = new PassThroughUseCase<ICommandCookiePreferences, TPortOutcome>((ctx, command) => consent.applyManualConsent(ctx, command));
    this.consentChanged = new ConsentChangedUseCase(side);
  }
}
